import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

/**
 * "Remove this medicine?" — Figma 3500:2856, shown by the × on a consultation
 * medicine row.
 *
 * The copy is deliberately blunt about scope: removing here also deletes the
 * entry from the patient's Medicines table, which is why unticking was taken
 * away and replaced by this.
 *
 * Presentational — the host owns the API call. It reports back through `busy`
 * and `error`; the dialog stays open on failure so the doctor sees why, and
 * every exit (Escape, backdrop, ×, Cancel) is sealed while the request is in
 * flight, because a delete that is already on the wire cannot be called off.
 *
 * Reuses .rx-overlay/.rx-modal from the publish dialog; only the pieces the
 * board draws differently (close button, danger button, equal-width footer)
 * are new. Escape cancels, unlike the publish dialog — this one is destructive
 * and a backdrop-only exit is a poor last resort.
 */
const RemoveMedicineDialog = ({ medicineName, busy, error, onConfirm, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  return ReactDOM.createPortal(
    <div
      className="rx-overlay"
      onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}
    >
      <div
        className="rx-modal is-confirm"
        role="dialog"
        aria-modal="true"
        aria-label="Remove this medicine?"
      >
        <div className="rx-confirm-head">
          <h2 className="rx-confirm-title">Remove this medicine?</h2>
          <button
            type="button"
            className="rx-confirm-close"
            aria-label="Close"
            disabled={busy}
            onClick={onClose}
          >
            <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path d="M3 3l8 8M11 3l-8 8" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <p className="rx-confirm-body">
          {medicineName} will be removed from this prescription and from the
          patient&apos;s Medicines table.
        </p>

        {error ? <p className="rx-note is-error">{error}</p> : null}

        <div className="rx-confirm-actions">
          <button
            type="button"
            className="rx-confirm-btn is-secondary"
            disabled={busy}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rx-confirm-btn is-danger"
            disabled={busy}
            onClick={onConfirm}
          >
            {busy ? "Removing…" : "Remove medicine"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

RemoveMedicineDialog.propTypes = {
  /** Named in the body copy, exactly as the row shows it. */
  medicineName: PropTypes.string,
  /** True while the delete is in flight — seals every exit. */
  busy: PropTypes.bool,
  /** Shown in place, so a failure doesn't dismiss the dialog. */
  error: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default RemoveMedicineDialog;
